import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { useThemeMode } from "./ThemeModeContext";
import {
	getUserSettings,
	updateUserSettings
} from "../firebase/settings/userSettingsRepository";

interface UserSettingsContextValue {
	maskProfit: boolean;
	setMaskProfit: (value: boolean) => void;
}

const UserSettingsContext = createContext<UserSettingsContextValue | undefined>(
	undefined
);

export const UserSettingsProvider: React.FC<React.PropsWithChildren> = ({
	children
}) => {
	const { user } = useAuth();
	const { mode, setMode } = useThemeMode();
	const [maskProfit, setMaskProfitState] = useState(true);
	const [hasLoadedSettings, setHasLoadedSettings] = useState(false);
	const [loadedSettingsUserId, setLoadedSettingsUserId] = useState<
		string | null
	>(null);

	useEffect(() => {
		let isActive = true;

		const loadSettings = async () => {
			setHasLoadedSettings(false);
			setLoadedSettingsUserId(null);

			if (!user?.uid) {
				setHasLoadedSettings(true);
				return;
			}

			const persistedSettings = await getUserSettings(user.uid);
			if (!isActive) {
				return;
			}

			if (
				persistedSettings.themeMode === "light" ||
				persistedSettings.themeMode === "dark"
			) {
				setMode(persistedSettings.themeMode);
			}
			setMaskProfitState(persistedSettings.maskProfit ?? true);
			setLoadedSettingsUserId(user.uid);
			setHasLoadedSettings(true);
		};

		void loadSettings();
		return () => {
			isActive = false;
		};
	}, [setMode, user?.uid]);

	useEffect(() => {
		if (
			!user?.uid ||
			!hasLoadedSettings ||
			loadedSettingsUserId !== user.uid
		) {
			return;
		}

		void updateUserSettings(user.uid, {
			themeMode: mode,
			maskProfit
		});
	}, [hasLoadedSettings, loadedSettingsUserId, maskProfit, mode, user?.uid]);

	const setMaskProfit = (value: boolean) => {
		setMaskProfitState(value);
	};

	return (
		<UserSettingsContext.Provider value={{ maskProfit, setMaskProfit }}>
			{children}
		</UserSettingsContext.Provider>
	);
};

export const useUserSettings = () => {
	const context = useContext(UserSettingsContext);
	if (!context) {
		throw new Error(
			"useUserSettings must be used within a UserSettingsProvider"
		);
	}
	return context;
};
